import { Box, Card, CardContent, CardHeader, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import React from 'react'
import OpenWithRoundedIcon from '@mui/icons-material/OpenWithRounded';
import { SparkLineChart } from '@mui/x-charts';
import LineChartModal from './LineChartModal';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

interface StatSparklineCardProps {
    stat: any,
    timeframe: string,
    cumulative: boolean
    statNameLookup: any,
    handleExpandChart: (stat) => void
}

const StatSparklineCard = (props: StatSparklineCardProps) => {
    const { stat, timeframe, cumulative, statNameLookup, handleExpandChart } = props;
    const { t } = useTranslation()
    const renderValue = (metric, timeframe) => {
        if (metric[timeframe] == false) {
            return (
                <Typography variant="h4" sx={{ color: 'black', fontWeight: 600, textAlign: 'center' }}>N/A</Typography>
            )
        }
        // Sum up all values in array
        let val = metric[timeframe].reduce((a, b) => a + b.value, 0);
        let suffix = null;
        if (metric.name === 'storage_usage') {
            suffix = 'MB';
            if (val > 1000000000) {
                val = (val / 1000000000).toFixed(2);
                suffix = 'GB';
            } else {
                val = (val / 1000000).toFixed(2);
            }
        } else {
            // if value is over 1 thousand, convert to thousands, 1 million, etc.
            if (val > 1000000) {
                val = (val / 1000000).toFixed(2);
                suffix = 'M';
            } else if (val > 1000) {
                val = (val / 1000).toFixed(2);
                suffix = 'K';
            }
        }
        return (
            <Stack direction="row" gap={0} alignItems="baseline" justifyContent="center">
                <Typography variant="h4" sx={{ color: 'black', fontWeight: 600, textAlign: 'center' }}>{val}</Typography>
                <Typography variant="h6" sx={{ color: 'black', fontWeight: 600, textAlign: 'center' }}>{suffix}</Typography>
            </Stack>
        )
    };

    const renderSparkline = (metric, timeframe) => {
        if (metric[timeframe] == false) {
            return [0];
        }
        if (metric[timeframe] instanceof Array) {
            if (cumulative) {
                // create list containing cumulative value of each value in object in array
                return metric[timeframe].reduce((a, b) => [...a, a[a.length - 1] + b.value], [0]);
            } else {
                return metric[timeframe].map((item) => item.value);
            }
        }
    }

    return (
        <>
            <Card variant="outlined" sx={{ minHeight: 125 }}>
                <CardHeader
                    action={<Tooltip title="Expand"><IconButton aria-label="expand" size="small" onClick={() => handleExpandChart(stat)}><OpenWithRoundedIcon fontSize='inherit' /></IconButton></Tooltip>}
                    subheader={t(statNameLookup[stat.name])}
                />
                <CardContent>
                    <Stack direction="column" gap={2} sx={{ justifyContent: 'space-between', height: '100%' }}>
                        <Stack direction="row" gap={1} sx={{ justifyContent: 'space-around', }}>
                            <Box width="40%">
                                {renderValue(stat, timeframe)}
                            </Box>
                            <Box width="60%" sx={{ flexGrow: 1 }}>
                                <SparkLineChart
                                    sx={{
                                        '& .MuiLineElement-root': {
                                            strokeWidth: 2,
                                            color: 'rgba(92, 168, 153, 1)',
                                            opacity: 0.75,
                                        },
                                        '& .MuiAreaElement-series-auto-generated-id-0': {
                                            fill: "url('#myGradient')",
                                        },
                                    }}
                                    height={50}
                                    data={renderSparkline(stat, timeframe)}
                                    area={true}
                                >
                                    <defs>
                                        <linearGradient id="myGradient" gradientTransform="rotate(90)">
                                            <stop offset="10%" stopColor="rgba(92, 168, 153, 0.22)" />
                                            <stop offset="100%" stopColor="rgba(92, 168, 153, 0)" />
                                        </linearGradient>
                                    </defs>
                                </SparkLineChart>
                            </Box>
                        </Stack>
                        <Typography variant="caption" >{t('last updated')}: {stat?.last_updated ? moment.utc(stat?.last_updated).local().format("LLL") : "N/A"}</Typography>
                    </Stack>
                </CardContent>
            </Card>
        </>
    )
}

export default StatSparklineCard
